import { Check } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { useAuth, API } from "../context/AuthContext";

export default function PlanCard({ plan }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const handleOrder = async () => {
    if (!user) {
      toast({
        title: "Please sign in",
        description: "Create a free account to place your order.",
      });
      navigate("/login", { state: { from: "/plans" } });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        {
          lookup_key: plan.id,
          quantity: 1,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (err) {
      toast({
        title: "Could not start checkout",
        description: err?.response?.data?.detail || err.message || "Please try again.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`plan-card relative flex flex-col rounded-2xl border p-6 md:p-7 bg-white ${
        plan.highlight ? "border-green-600 shadow-[0_10px_30px_-15px_rgba(22,163,74,0.35)]" : "border-neutral-200"
      }`}
    >
      {plan.highlight && (
        <Badge className="absolute -top-3 left-6 bg-green-600 hover:bg-green-600 text-white rounded-full px-3 py-1">
          Most Popular
        </Badge>
      )}
      <h3 className="text-lg font-bold text-neutral-900">{plan.name}</h3>
      <div className="mt-3 flex items-baseline gap-1">
        {plan.oldPrice && (
          <span className="text-neutral-400 line-through text-lg mr-1">NZ${plan.oldPrice}</span>
        )}
        <span className="text-3xl md:text-4xl font-extrabold text-neutral-900">NZ${plan.price}</span>
        <span className="text-sm text-neutral-500 font-medium">{plan.unit}</span>
      </div>

      <ul className="mt-5 space-y-2.5 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
            <Check size={16} className="mt-0.5 shrink-0 text-green-600" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={handleOrder}
        disabled={submitting}
        className={`mt-6 rounded-full ${
          plan.highlight
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-neutral-900 hover:bg-neutral-800 text-white"
        }`}
      >
        {submitting ? "Redirecting to secure checkout…" : "Choose Plan"}
      </Button>
    </div>
  );
}
